import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Button, Container, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import OpenGraph from '@/components/OpenGraph';
import useFavorites from '@/src/hooks/useFavorites';
import FullPage from '@/src/components/UI/FullPage';
import { ListingSkeleton } from '@/src/components/UI/Listing';
import Listings from '@/src/components/UI/Listings';
import FunwooAPI, { ListingDetail } from '@/swagger';

function EmptyResult() {
  return (
    <Container>
      <Flex direction='column' justify='center' align='center' height={200}>
        <Text>尚無收藏的物件</Text>
        <Link href='/buy' passHref>
          <Button variant='primary' mt={8}>
            開始找屋
          </Button>
        </Link>
      </Flex>
    </Container>
  );
}

function FavoriteView({
  favorites,
  onFavButtonPressCallBack,
}: {
  favorites: Array<ListingDetail>;
  onFavButtonPressCallBack: (sid: string, isFav: boolean) => void;
}) {
  if (favorites.length === 0) {
    return <EmptyResult />;
  }
  return <Listings onFavButtonPressCallBack={onFavButtonPressCallBack} data={favorites} />;
}

function FavoriteSkeleton({ sids }: { sids: string[] }) {
  return (
    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={8}>
      {sids.map((sid) => (
        <ListingSkeleton key={sid} />
      ))}
    </SimpleGrid>
  );
}

export default function FavoriteContainer() {
  const { sids, add, remove } = useFavorites();
  const [loading, setLoading] = useState(true);
  const [favoriteListings, setFavoriteListings] = useState<Array<ListingDetail>>([]);

  useEffect(() => {
    (async () => {
      try {
        const listings = await FunwooAPI.listingApi.findAllFavorite({ sids });
        setFavoriteListings(listings.data);
      } catch (ex) {
        // nada
      } finally {
        setLoading(false);
      }
    })();
  }, [sids]);

  const onFavButtonPressCallBack = useCallback(
    (sid, isFav) => {
      if (isFav) {
        add(sid);
      } else {
        remove(sid);
      }
    },
    [sids, add, remove]
  );

  /**
   * there is a moment where loading equals to false but
   * favoriteListings is not being set yet.
   */
  const ready = !loading && sids.length === favoriteListings.length;

  return (
    <FullPage title={''} hasHeaderPlaceHolder>
      <OpenGraph title='FUNWOO 德載國際不動產 - 我的最愛' type='funwoo.favorite' url='https://funwoo.com.tw/favorite' />
      {ready ? (
        <FavoriteView onFavButtonPressCallBack={onFavButtonPressCallBack} favorites={favoriteListings} />
      ) : (
        <FavoriteSkeleton sids={sids} />
      )}
    </FullPage>
  );
}

export const getStaticProps = async ({ locale }: { locale: string }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};
